import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { MessageSquareMore, X, Send, CheckCircle } from 'lucide-react';
import CustomSelect from './shared/CustomSelect';

interface FeedbackPopupProps {
  isVisible: boolean;
}

const FeedbackPopup: React.FC<FeedbackPopupProps> = ({ isVisible }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [formData, setFormData] = useState({
    agent: '',
    description: ''
  });

  const agentOptions = [
    'Lead Gen Agent',
    'Call Agent',
    'WhatsApp Agent',
    'Email Agent',
    'LinkedIn Agent'
  ];

  const handleOpenModal = () => {
    setIsModalOpen(true);
    setShowThankYou(false);
    setFormData({
      agent: '',
      description: ''
    });
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setShowThankYou(false);
    setFormData({
      agent: '',
      description: ''
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.agent || !formData.description) {
      return;
    }

    setIsSubmitting(true);

    try {
      const webhookData = {
        time_stamp: new Date().toISOString(),
        agent: formData.agent,
        description: formData.description
      };

      console.log('[FeedbackPopup] Sending feedback webhook:', webhookData);

      await fetch('https://hook.eu2.make.com/o2axjqvuc5cixql4v0y5k16w8ez81r71', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(webhookData)
      });

      console.log('[FeedbackPopup] Feedback webhook sent successfully');
      setShowThankYou(true);

      setTimeout(() => {
        handleCloseModal();
      }, 3000);

    } catch (error) {
      console.error('[FeedbackPopup] Failed to send feedback webhook:', error);
      setShowThankYou(true);
      setTimeout(() => {
        handleCloseModal();
      }, 3000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (!isVisible) return null;

  return (
    <>
      <button
        onClick={handleOpenModal}
        className="fixed bottom-6 right-6 w-14 h-14 bg-gradient-to-br from-[#1875FF] to-[#05E0D1] text-white rounded-full shadow-lg hover:shadow-xl hover:scale-105 focus:ring-4 focus:ring-[#1875FF]/30 transition-all duration-200 z-40 flex items-center justify-center group"
        title="Share Feedback"
      >
        <MessageSquareMore size={24} className="group-hover:scale-110 transition-transform" />
      </button>

      {isModalOpen && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full overflow-hidden shadow-2xl">
            <div className="flex items-center justify-between p-5 border-b border-[#05E0D1]/20 bg-gradient-to-r from-[#1875FF]/5 to-[#05E0D1]/5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#1875FF] to-[#05E0D1] flex items-center justify-center shadow-md">
                  <MessageSquareMore size={20} className="text-white" />
                </div>
                <h3 className="text-xl font-extrabold text-gray-900 font-display">Share Your Feedback</h3>
              </div>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-600 transition-colors rounded-lg p-1 hover:bg-gray-100"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6">
              {showThankYou ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="text-[#05E0D1]" size={32} />
                  </div>
                  <h4 className="text-xl font-extrabold text-gray-900 font-display mb-2">Thank You!</h4>
                  <p className="text-gray-600">
                    Your feedback has been submitted successfully. We appreciate your input!
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label htmlFor="agent" className="block text-sm font-semibold text-gray-700 mb-2">
                      Which agent are you reporting about? <span className="text-[#1875FF]">*</span>
                    </label>
                    <CustomSelect
                      id="agent"
                      name="agent"
                      value={formData.agent}
                      onChange={(value) => handleSelectChange('agent', value)}
                      options={agentOptions.map(agent => ({ value: agent, label: agent }))}
                      placeholder="Select an agent"
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="description" className="block text-sm font-semibold text-gray-700 mb-2">
                      Describe the issue you are facing or list your suggestions <span className="text-[#1875FF]">*</span>
                    </label>
                    <textarea
                      id="description"
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      required
                      rows={4}
                      placeholder="Please describe the issue or provide your suggestions..."
                      className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#1875FF]/30 focus:border-[#1875FF] transition-all resize-none"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting || !formData.agent || !formData.description}
                    className="w-full bg-gradient-to-r from-[#1875FF] to-[#05E0D1] text-white py-3 px-4 rounded-xl font-semibold hover:shadow-lg hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:hover:shadow-none transition-all duration-200 flex items-center justify-center space-x-2"
                  >
                    {isSubmitting ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <>
                        <Send size={18} />
                        <span>Submit</span>
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default FeedbackPopup;
